import React, { useEffect } from "react";
import { Router } from "@reach/router";
import { useGovernor } from "@techempower/react-governor";
import LoginPage from "../pages/login";
import UserService from "../services/user_service";
import {
  default_account_profile,
  default_company_profile
} from "../schema/profile";
import {
  setAccessToken,
  removeAccessToken,
  logSession,
  getAccessToken
} from "../services/utils";
import SignUpPage from "../pages/sign_up";

const UserContext = React.createContext();

/**
 * Helper to format user profile from the backend into account profile for the
 * frontend.
 */
export function processUserProfile(userProfile) {
  const profile = { ...userProfile };
  const content = profile.content;
  delete profile.content;

  return {
    ...default_account_profile,
    ...(content ? JSON.parse(content) : {}),
    ...profile
  };
}

export function processCompanyProfile(companyProfile) {
  const profile = { ...companyProfile };
  const content = profile.content;
  delete profile.content;

  return {
    ...default_company_profile,
    ...(content ? JSON.parse(content) : {}),
    ...profile
  };
}

const initialState = {
  isLoggedIn: UserService.isLoggedIn(),
  accountProfile: { ...default_account_profile },
  companyProfile: { ...default_company_profile }
};

const contract = {
  /**
   * Attempts to login the user given by username and password.
   * @param {*} username
   * @param {*} password
   */
  async login(username, password, setErrorMessage) {
    try {
      const success = await UserService.login(username, password);

      if (success) {
        return state => ({
          ...state,
          isLoggedIn: true
        });
      } else {
        // TODO - handle this better
        setErrorMessage("Failed login");
      }
    } catch (e) {
      // TODO - handle this better
      setErrorMessage(e.statusText);
    }
    return state => ({
      ...state,
      isLoggedIn: false
    });
  },

  async setAccessToken(token) {
    if (token) {
      setAccessToken(token);
      return state => ({
        ...state,
        isLoggedIn: true
      });
    }
    // A falsy token here used to call removeAccessToken(), which silently logged
    // the user out — and, because localStorage is shared across every tab in the
    // browser, dropped ALL tabs to the login page on their next reload. That could
    // fire from a sign-up/edge response that arrived without a token. Never drop an
    // existing session on a falsy value; only an explicit logout clears it.
    return state => ({
      ...state
    });
  },

  /**
   * Attempts to logout the currently logged in user out.
   */
  async logout(setErrorMessage) {
    try {
      await UserService.logout();
    } catch (e) {
      // TODO - handle this better
      setErrorMessage(e.statusText);
    }

    return state => ({
      ...state,
      isLoggedIn: false
    });
  },

  async getProfile() {
    try {
      const profile = await UserService.getProfile();
      return state => ({
        ...state,
        accountProfile: processUserProfile(profile.userProfile),
        companyProfile: processCompanyProfile(profile.companyProfile)
      });
    } catch (e) {
      // TODO
    }
    return state => ({
      ...state
    });
  },

  async setAccountProfile(accountProfile) {
    return state => ({
      ...state,
      accountProfile: accountProfile
    });
  },

  async saveAccountProfile(accountProfile) {
    try {
      const profile = await UserService.postUserProfile(accountProfile);
      return state => ({
        ...state,
        accountProfile: processUserProfile(profile)
      });
    } catch (e) {
      // TODO
    }
    return state => ({
      ...state
    });
  },

  async setCompanyProfile(companyProfile) {
    return state => ({
      ...state,
      companyProfile: companyProfile
    });
  },

  async saveCompanyProfile(companyProfile, companyLogo) {
    try {
      const profile = await UserService.postCompanyProfile(
        companyProfile,
        companyLogo
      );
      return state => ({
        ...state,
        companyProfile: processCompanyProfile(profile),
        companyProfileError: null
      });
    } catch (e) {
      // Surface the failure instead of swallowing it. The company profile is written
      // through the company-admin endpoint, so a non-admin user may be rejected —
      // callers (e.g. the Realtor Directory auto-add) can show this to the user.
      return state => ({
        ...state,
        companyProfileError:
          (e && e.message) || "Could not save company information."
      });
    }
  }
};

/**
 * UserProvider is a context provider of the user state. Children (at
 * any depth) may call `useUser()`.
 * @see `useUser`
 * @param {*} props
 */
export function UserProvider(props) {
  const [state, actions] = useGovernor(initialState, contract);

  // One-time boot record: did this page load start with a session token or not?
  // Lets us tell "landed on login because the session was cleared" apart from
  // "landed on login because this was simply a fresh/cleared browser".
  useEffect(() => {
    logSession("app-boot token=" + (getAccessToken() ? "present" : "ABSENT"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.isLoggedIn) {
      actions.getProfile();
    }
  }, [state.isLoggedIn]);

  return (
    <UserContext.Provider value={[state, actions]}>
      {state.isLoggedIn ? props.children : (
        <Router>
          <SignUpPage path="/sign-up" />
          <LoginPage default />
        </Router>
      )}
    </UserContext.Provider>
  );
}

/**
 * Custom hook to consume the provided user context.
 * @returns {Object[]} [state, actions]
 */
export function useUser() {
  return React.useContext(UserContext);
}
